'use client';

import gsap from 'gsap';
import Image from 'next/image';
import Link from 'next/link';
import {
  SyntheticEvent,
  FC,
  memo,
  JSX,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { twMerge } from 'tailwind-merge';

// Constants
const ANIMATION_DEFAULTS = {
  delay: 0,
  duration: 8000,
  pause: 0,
  ease: 'cubic-bezier(1,0,0,1)',
} as const;

const styles = {
  item: 'w-full h-[180px] relative overflow-hidden rounded-xl bg-fill-zinc900 border-1 border-gray flex items-center justify-center',
  itemSmall: 'h-[86px]',
  preview:
    'w-full h-full absolute z-[1] object-cover object-center pointer-events-none opacity-0',
  info: 'relative z-[3] flex flex-col items-center justify-center gap-2',
  name: 'text-sm text-center opacity-50 hyphens-manual',
  gradient: 'absolute inset-0 bg-gradient-tool z-[2]',
} as const;

type TToolCardProps = {
  image: string;
  name: string;
  icon: JSX.Element;
  handleClickImage: (event: SyntheticEvent) => void;
  isSmall?: boolean;
  href?: string;
  disabled?: boolean;
  delay?: number;
  duration?: number;
  pause?: number;
};

// Separate content component to reduce re-renders
const ToolCardContent: FC<{
  icon: JSX.Element;
  name: string;
  image: string;
  refs: {
    infoRef: React.RefObject<HTMLDivElement | null>;
    iconRef: React.RefObject<HTMLDivElement | null>;
    titleRef: React.RefObject<HTMLDivElement | null>;
    previewRef: React.RefObject<HTMLImageElement | null>;
  };
}> = memo(({ icon, name, image, refs }) => (
  <>
    <div className={styles.info} ref={refs.infoRef}>
      <div ref={refs.iconRef}>{icon}</div>
      <div
        ref={refs.titleRef}
        className={styles.name}
        dangerouslySetInnerHTML={{ __html: name }}
      />
    </div>
    <Image
      width={152}
      height={152}
      src={image}
      alt={name}
      className={styles.preview}
      priority
      quality={95}
      ref={refs.previewRef}
    />
    <div className={styles.gradient} />
  </>
));

ToolCardContent.displayName = 'ToolCardContent';

export const ToolCard: FC<TToolCardProps> = memo(
  ({
    icon,
    image,
    name,
    handleClickImage,
    href,
    isSmall,
    disabled,
    delay = ANIMATION_DEFAULTS.delay,
    duration = ANIMATION_DEFAULTS.duration,
    pause = ANIMATION_DEFAULTS.pause,
  }) => {
    // Create refs individually
    const infoRef = useRef<HTMLDivElement | null>(null);
    const previewRef = useRef<HTMLImageElement | null>(null);
    const iconRef = useRef<HTMLDivElement | null>(null);
    const titleRef = useRef<HTMLDivElement | null>(null);

    // Group refs in a memoized object for passing to child component
    const refs = useMemo(
      () => ({
        infoRef,
        previewRef,
        iconRef,
        titleRef,
      }),
      [],
    );

    // Memoize animation values
    const animationConfig = useMemo(
      () => ({
        delaySeconds: delay / 1000,
        durationSeconds: duration / 1000,
        pauseSeconds: pause / 1000,
      }),
      [delay, duration, pause],
    );

    // Animation effect
    useEffect(() => {
      const { delaySeconds, durationSeconds, pauseSeconds } = animationConfig;

      // Early return if refs are not ready
      if (
        !refs.infoRef.current ||
        !refs.iconRef.current ||
        !refs.titleRef.current ||
        !refs.previewRef.current
      ) {
        return;
      }

      const timeline = gsap.timeline({
        delay: delaySeconds,
        repeat: -1,
        yoyo: true,
        repeatDelay: pauseSeconds,
        defaults: {
          duration: durationSeconds / 2,
          ease: ANIMATION_DEFAULTS.ease,
        },
      });

      // Batch animations for better performance
      timeline
        .to(refs.infoRef.current, { y: '20px' }, 0)
        .to(refs.iconRef.current, { opacity: 0 }, 0)
        .to(refs.titleRef.current, { opacity: 1 }, 0)
        .to(refs.previewRef.current, { opacity: 1 }, 0);

      return () => {
        timeline.kill();
      };
    }, [animationConfig, refs]);

    // Memoize className
    const className = useMemo(
      () =>
        twMerge(
          styles.item,
          isSmall && styles.itemSmall,
          disabled && 'pointer-events-none',
        ),
      [isSmall, disabled],
    );

    // Memoize content
    const content = useMemo(
      () => (
        <ToolCardContent icon={icon} name={name} image={image} refs={refs} />
      ),
      [icon, name, image, refs],
    );

    // Optimize click handler
    const onClick = useCallback(
      (e: SyntheticEvent) => {
        if (!disabled) {
          handleClickImage(e);
        }
      },
      [handleClickImage, disabled],
    );

    // Render appropriate element
    if (href) {
      return (
        <Link
          className={className}
          href={href}
          onClick={onClick}
          aria-disabled={disabled}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={className}
        onClick={onClick}
        disabled={disabled}
        type="button"
        aria-label={`Tool: ${name}`}
      >
        {content}
      </button>
    );
  },
  // Custom comparison function for memo
  (prevProps, nextProps) =>
    prevProps.image === nextProps.image &&
    prevProps.name === nextProps.name &&
    prevProps.isSmall === nextProps.isSmall &&
    prevProps.href === nextProps.href &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.delay === nextProps.delay &&
    prevProps.duration === nextProps.duration &&
    prevProps.pause === nextProps.pause &&
    prevProps.handleClickImage === nextProps.handleClickImage &&
    prevProps.icon === nextProps.icon,
);

ToolCard.displayName = 'ToolCard';
